import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../components/Button.css';

const MAX_LOGO_BYTES = 560 * 1024; // ~560 KB daadwerkelijke grootte
const ALLOWED_LOGO_MIMES = /^image\/(png|jpe?g|gif|webp|svg\+xml)$/i;

function readFileAsLogo(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('Geen bestand gekozen.'));
    if (!ALLOWED_LOGO_MIMES.test(file.type || '')) {
      return reject(new Error('Logo moet PNG, JPG, GIF, WEBP of SVG zijn.'));
    }
    if (file.size > MAX_LOGO_BYTES) {
      return reject(new Error(`Logo is te groot (${Math.round(file.size / 1024)} KB, max 560 KB).`));
    }
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Kon bestand niet lezen'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      const b64 = comma >= 0 ? result.slice(comma + 1) : result;
      resolve({
        name: file.name || 'logo.png',
        contentType: file.type || 'image/png',
        contentBase64: b64,
      });
    };
    reader.readAsDataURL(file);
  });
}

function LogoField({ label, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const logo = await readFileAsLogo(file);
      onChange(logo);
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };
  const previewSrc =
    value && value.contentBase64
      ? `data:${value.contentType || 'image/png'};base64,${value.contentBase64}`
      : '';
  return (
    <div
      style={{
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
        fontSize: '0.85rem',
        marginTop: '0.35rem',
      }}
    >
      <span style={{ color: '#374151' }}>{label}:</span>
      {previewSrc ? (
        <img
          src={previewSrc}
          alt="logo"
          style={{
            maxHeight: 48,
            maxWidth: 200,
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            padding: 2,
            background: '#fff',
          }}
        />
      ) : (
        <span style={{ color: '#9ca3af' }}>— geen logo —</span>
      )}
      <label
        className="btn btn-secondary"
        style={{ padding: '0.25rem 0.6rem', cursor: busy ? 'wait' : 'pointer', fontSize: '0.8rem' }}
      >
        {busy ? 'Laden…' : value ? 'Vervangen' : 'Logo uploaden'}
        <input type="file" accept="image/*" onChange={onPick} disabled={busy} style={{ display: 'none' }} />
      </label>
      {value ? (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => onChange(null)}
          style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
        >
          Verwijderen
        </button>
      ) : null}
    </div>
  );
}

export default function TicketsInstellingen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);
  const [aiContext, setAiContext] = useState('');
  const [defaultSignature, setDefaultSignature] = useState('');
  const [defaultSignatureLogo, setDefaultSignatureLogo] = useState(null);
  const [mailboxSignatures, setMailboxSignatures] = useState({});
  const [mailboxSignatureLogos, setMailboxSignatureLogos] = useState({});
  // Standaard antwoorden (canned replies): client gebruikt een lokaal uniek
  // key-veld zodat we zonder id's toch rijen kunnen reorderen/verwijderen voordat
  // alles in één keer naar de server gaat.
  const [cannedReplies, setCannedReplies] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        try {
          const info = await api.get('/tickets/outlook-info');
          setMailboxes(Array.isArray(info.data?.mailboxes) ? info.data.mailboxes : []);
        } catch (_) {
          setMailboxes([]);
        }
        try {
          const settings = await api.get('/tickets/settings');
          setAiContext(settings.data?.aiContext || '');
          setDefaultSignature(settings.data?.defaultSignature || '');
          setDefaultSignatureLogo(settings.data?.defaultSignatureLogo || null);
          setMailboxSignatures(settings.data?.mailboxSignatures || {});
          setMailboxSignatureLogos(settings.data?.mailboxSignatureLogos || {});
        } catch (_) {
          setAiContext('');
          setDefaultSignature('');
          setDefaultSignatureLogo(null);
          setMailboxSignatures({});
          setMailboxSignatureLogos({});
        }
        try {
          const canned = await api.get('/tickets/canned-replies');
          const list = Array.isArray(canned.data?.replies) ? canned.data.replies : [];
          setCannedReplies(
            list.map((r, i) => ({
              key: `srv-${r.id ?? i}`,
              title: String(r.title || ''),
              body: String(r.body || ''),
            }))
          );
        } catch (_) {
          setCannedReplies([]);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleMailboxSignatureChange = (mailbox, value) => {
    setMailboxSignatures((prev) => ({ ...prev, [mailbox]: value }));
  };

  const handleMailboxLogoChange = (mailbox, logo) => {
    setMailboxSignatureLogos((prev) => {
      const next = { ...prev };
      if (logo) next[mailbox] = logo;
      else delete next[mailbox];
      return next;
    });
  };

  const handleAddCannedReply = () => {
    setCannedReplies((prev) => [
      ...prev,
      { key: `new-${Date.now()}-${prev.length}`, title: '', body: '' },
    ]);
  };

  const handleUpdateCannedReply = (key, field, value) => {
    setCannedReplies((prev) =>
      prev.map((r) => (r.key === key ? { ...r, [field]: value } : r))
    );
  };

  const handleRemoveCannedReply = (key) => {
    setCannedReplies((prev) => prev.filter((r) => r.key !== key));
  };

  const handleMoveCannedReply = (key, direction) => {
    setCannedReplies((prev) => {
      const idx = prev.findIndex((r) => r.key === key);
      if (idx < 0) return prev;
      const swap = direction === 'up' ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = {};
      Object.entries(mailboxSignatures || {}).forEach(([mailbox, text]) => {
        const key = String(mailbox || '').trim();
        const val = String(text || '').trim();
        if (key && val) cleaned[key] = val;
      });
      await api.put('/tickets/settings', {
        aiContext,
        defaultSignature,
        defaultSignatureLogo,
        mailboxSignatures: cleaned,
        mailboxSignatureLogos,
      });
      // Standaard antwoorden in aparte endpoint zodat de server-logica zuiver blijft.
      const cannedPayload = cannedReplies
        .map((r) => ({
          title: String(r.title || '').trim(),
          body: String(r.body || ''),
        }))
        .filter((r) => r.title && r.body.trim());
      await api.put('/tickets/canned-replies', { replies: cannedPayload });
      setMailboxSignatures(cleaned);
      setCannedReplies(
        cannedPayload.map((r, i) => ({ key: `srv-saved-${Date.now()}-${i}`, ...r }))
      );
      alert('Instellingen opgeslagen.');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Laden...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 820 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate('/tickets')}
          style={{ padding: '0.35rem 0.6rem' }}
        >
          ← Terug
        </button>
        <h2 style={{ margin: 0, color: '#00B8A9' }}>Ticket-instellingen</h2>
      </div>

      <section
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        <h3 style={{ margin: 0 }}>AI-context (jouw account)</h3>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>
          Deze tekst wordt altijd meegestuurd aan de AI bij analyseren, antwoord genereren en vertalen. Gebruik dit om
          standaardinstructies voor jouw werk vast te leggen (tone-of-voice, hoe om te gaan met klachten, etc.).
        </p>
        <textarea
          value={aiContext}
          onChange={(e) => setAiContext(e.target.value)}
          rows={6}
          style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.92rem' }}
          placeholder="Bijv. Houd antwoorden kort en vriendelijk. Noem gratis verzending en 30 dagen retour. Verwijs klachten naar Sabine…"
        />
      </section>

      <section
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <h3 style={{ margin: 0 }}>E-mail handtekeningen</h3>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>
          De handtekening wordt automatisch onder jouw antwoord geplaatst. Een logo verschijnt direct onder de
          signature-tekst in de verzonden mail (als inline-afbeelding). Mailbox-specifieke instellingen hebben
          voorrang op de standaard.
        </p>

        <div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.88rem' }}>
            Standaard (fallback voor alle mailboxen)
            <textarea
              value={defaultSignature}
              onChange={(e) => setDefaultSignature(e.target.value)}
              rows={4}
              style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.92rem' }}
              placeholder={'Met vriendelijke groet,\nJouw naam\n4Shine'}
            />
          </label>
          <LogoField
            label="Standaard logo"
            value={defaultSignatureLogo}
            onChange={setDefaultSignatureLogo}
          />
        </div>

        {mailboxes.length === 0 ? (
          <p style={{ margin: 0, color: '#b45309', fontSize: '0.85rem' }}>
            Geen mailboxen gevonden. Controleer `OUTLOOK_MAILBOXES` op de server.
          </p>
        ) : (
          mailboxes.map((m) => (
            <div key={m}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.88rem' }}>
                Handtekening voor <span style={{ color: '#0369a1' }}>{m}</span>
                <textarea
                  value={mailboxSignatures[m] || ''}
                  onChange={(e) => handleMailboxSignatureChange(m, e.target.value)}
                  rows={4}
                  style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.92rem' }}
                  placeholder="Leeg laten = gebruik standaard handtekening"
                />
              </label>
              <LogoField
                label="Logo"
                value={mailboxSignatureLogos[m] || null}
                onChange={(logo) => handleMailboxLogoChange(m, logo)}
              />
            </div>
          ))
        )}
      </section>

      <section
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Standaard antwoorden</h3>
          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            {cannedReplies.length} / 50
          </span>
        </div>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>
          Teksten die je vaak stuurt. Op de tickets-pagina verschijnen ze onder het antwoordveld als
          knoppen; bij klikken wordt de tekst op de cursorpositie ingevoegd. Handtekening wordt
          automatisch alsnog toegevoegd bij verzenden — je hoeft die dus niet per standaard antwoord
          te herhalen.
        </p>

        {cannedReplies.length === 0 ? (
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#9ca3af' }}>
            Nog geen standaard antwoorden. Klik op "Toevoegen" om er één te maken.
          </p>
        ) : (
          cannedReplies.map((r, idx) => (
            <div
              key={r.key}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '0.75rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                background: '#f9fafb',
              }}
            >
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={r.title}
                  onChange={(e) => handleUpdateCannedReply(r.key, 'title', e.target.value)}
                  placeholder="Titel (bijv. Bedankje na bestelling)"
                  maxLength={120}
                  style={{
                    flex: '1 1 260px',
                    padding: '0.4rem 0.55rem',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: '0.9rem',
                  }}
                />
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleMoveCannedReply(r.key, 'up')}
                    disabled={idx === 0}
                    title="Omhoog"
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleMoveCannedReply(r.key, 'down')}
                    disabled={idx === cannedReplies.length - 1}
                    title="Omlaag"
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleRemoveCannedReply(r.key)}
                    title="Verwijderen"
                    style={{ padding: '0.25rem 0.55rem', fontSize: '0.8rem', color: '#b91c1c' }}
                  >
                    Verwijder
                  </button>
                </div>
              </div>
              <textarea
                value={r.body}
                onChange={(e) => handleUpdateCannedReply(r.key, 'body', e.target.value)}
                rows={4}
                placeholder="Hier de tekst die bij klikken in het antwoord wordt geplakt…"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  padding: '0.45rem 0.55rem',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                }}
              />
            </div>
          ))
        )}

        <div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleAddCannedReply}
            disabled={cannedReplies.length >= 50}
            style={{ padding: '0.35rem 0.7rem', fontSize: '0.85rem' }}
          >
            + Standaard antwoord toevoegen
          </button>
        </div>
      </section>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Opslaan…' : 'Instellingen opslaan'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/tickets')}>
          Annuleren
        </button>
      </div>
    </div>
  );
}
